import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { ArrowRight, MessageSquare, RefreshCw } from 'lucide-react';
import type { StepExamplesData, TopicExamplesData } from '../../types/journeys';

interface ExamplesDrawerProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  stepExamples?: StepExamplesData | null;
  topicExamples?: TopicExamplesData | null;
  mode: 'step' | 'topic';
}

export default function ExamplesDrawer({
  open,
  onClose,
  loading,
  stepExamples,
  topicExamples,
  mode,
}: ExamplesDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent side="bottom" className="h-[50vh] rounded-t-xl">
        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-300 mb-4" />
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            {mode === 'step' ? 'Transition Examples' : 'Topic Examples'}
          </SheetTitle>
          {mode === 'step' && stepExamples && (
            <SheetDescription className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {stepExamples.from_topic.label}
              </span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-medium text-gray-900">
                {stepExamples.to_topic.label}
              </span>
            </SheetDescription>
          )}
          {mode === 'topic' && topicExamples && (
            <SheetDescription>
              Examples for "{topicExamples.cluster.label}"
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-6 overflow-y-auto max-h-[calc(50vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : mode === 'step' && stepExamples ? (
            <StepExamplesContent examples={stepExamples} />
          ) : mode === 'topic' && topicExamples ? (
            <TopicExamplesContent examples={topicExamples} />
          ) : (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No examples found</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StepExamplesContent({ examples }: { examples: StepExamplesData }) {
  if (examples.examples.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No transition examples found</p>
        <p className="text-sm mt-1">
          Examples show actual conversations where users made this transition
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        These are actual conversations where users transitioned between these topics:
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        {examples.examples.map((example, idx) => (
          <div
            key={idx}
            className="bg-gray-50 rounded-lg p-4 border hover:border-blue-300 transition-colors"
          >
            <div className="space-y-3">
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">From:</div>
                <p className="text-gray-900 text-sm">"{example.from_value}"</p>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400 mx-auto" />
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">To:</div>
                <p className="text-gray-900 text-sm">"{example.to_value}"</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
              <span>User: {example.user_id}</span>
              {example.session_id && (
                <>
                  <span>|</span>
                  <span>Session: {example.session_id}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopicExamplesContent({ examples }: { examples: TopicExamplesData }) {
  return (
    <div className="space-y-6">
      {/* Example values */}
      {examples.example_values.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Common Variations</h4>
          <div className="space-y-2">
            {examples.example_values.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
              >
                <span className="text-sm text-gray-700">"{item.value}"</span>
                <Badge variant="secondary" className="text-xs">
                  {item.count}x
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Example sequences */}
      {examples.example_sequences.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Example Conversations</h4>
          <div className="space-y-4">
            {examples.example_sequences.map((sequence, idx) => (
              <div
                key={idx}
                className="p-4 bg-gray-50 rounded-lg border"
              >
                <div className="text-xs text-gray-500 mb-3">
                  Session: {sequence.session_id}
                </div>
                <div className="space-y-2">
                  {sequence.steps.map((step, stepIdx) => (
                    <div key={stepIdx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium shrink-0">
                        {stepIdx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-500">
                          {step.cluster_label}
                        </div>
                        <p className="text-sm text-gray-900 mt-0.5">
                          "{step.value}"
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
